import { useEffect, useState } from 'react';
import { Building2, LoaderCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/client';
import { usePharmacyProfile, useUpdatePharmacyProfile } from '@/api/hooks/usePharmacyProfile';
import type { Pharmacy } from '@/api/types';
import { useSession } from '@/auth/session';
import { PERMISSIONS } from '@/auth/permissions';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';

function toFormState(pharmacy: Pharmacy) {
  return {
    name: pharmacy.name,
    ownerName: pharmacy.ownerName ?? '',
    contactPhone: pharmacy.contactPhone ?? '',
    address: pharmacy.address ?? '',
    city: pharmacy.city ?? '',
    state: pharmacy.state ?? '',
    pincode: pharmacy.pincode ?? '',
    gstin: pharmacy.gstin ?? '',
    dlNo: pharmacy.dlNo ?? '',
  };
}

/**
 * The pharmacy's registration record — legal name, GSTIN, drug licence,
 * address. Distinct from StoreSettingsCard, which configures how bills are
 * printed rather than who's registered to print them. Superadmins have no
 * pharmacy of their own, so the card renders nothing for them.
 */
export function PharmacyProfileCard() {
  const { t } = useTranslation();
  const { can, isSuperadmin } = useSession();
  const canEdit = can(PERMISSIONS.settingsEdit);

  const { data, isLoading } = usePharmacyProfile();
  const updateProfile = useUpdatePharmacyProfile();

  const [form, setForm] = useState<ReturnType<typeof toFormState> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data) setForm(toFormState(data));
  }, [data]);

  function set<K extends keyof NonNullable<typeof form>>(key: K, value: string) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  async function handleSave() {
    if (!form) return;
    setError(null);

    try {
      await updateProfile.mutateAsync({
        name: form.name.trim(),
        ownerName: form.ownerName.trim(),
        contactPhone: form.contactPhone.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        pincode: form.pincode.trim(),
        gstin: form.gstin.trim(),
        dlNo: form.dlNo.trim(),
      });
      toast({ title: 'Pharmacy details saved', variant: 'success' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    }
  }

  if (isSuperadmin) return null;

  if (isLoading || !form) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">Loading…</CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Building2 className="h-4 w-4" /> Pharmacy registration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <fieldset disabled={!canEdit} className="space-y-4 disabled:opacity-60">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="p-name">Pharmacy name</Label>
              <Input id="p-name" value={form.name} onChange={(e) => set('name', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-owner">Owner name</Label>
              <Input id="p-owner" value={form.ownerName} onChange={(e) => set('ownerName', e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="p-phone">Contact phone</Label>
            <Input id="p-phone" value={form.contactPhone} onChange={(e) => set('contactPhone', e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="p-addr">{t('address')}</Label>
            <Input id="p-addr" value={form.address} onChange={(e) => set('address', e.target.value)} />
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="p-city">City</Label>
              <Input id="p-city" value={form.city} onChange={(e) => set('city', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-state">State</Label>
              <Input id="p-state" value={form.state} onChange={(e) => set('state', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-pincode">Pincode</Label>
              <Input id="p-pincode" value={form.pincode} onChange={(e) => set('pincode', e.target.value)} />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="p-gstin">GSTIN</Label>
              <Input id="p-gstin" value={form.gstin} onChange={(e) => set('gstin', e.target.value.toUpperCase())} maxLength={15} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-dl">Drug licence no.</Label>
              <Input id="p-dl" value={form.dlNo} onChange={(e) => set('dlNo', e.target.value)} />
            </div>
          </div>
        </fieldset>

        {error && (
          <p role="alert" className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired">
            {error}
          </p>
        )}

        {canEdit ? (
          <Button onClick={handleSave} disabled={updateProfile.isPending}>
            {updateProfile.isPending && <LoaderCircle className="animate-spin" />}
            Save pharmacy details
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground">You have view-only access to pharmacy registration details.</p>
        )}
      </CardContent>
    </Card>
  );
}
